import { useId, type ReactNode } from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export type FieldControlProps = {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

/**
 * Label + control + hint + error. The hint doubles as the error when `hintIsError` is set,
 * so a rule like "Use at least 12 characters." is shown once and turns red on failure.
 */
export function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  children: (props: FieldControlProps) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hintIsError = Boolean(error) && typeof hint === "string" && hint === error;
  const describedBy = [hint ? hintId : null, error && !hintIsError ? errorId : null].filter(Boolean).join(" ");
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      {children({ id, "aria-describedby": describedBy || undefined, "aria-invalid": error ? true : undefined })}
      {hint ? (
        <p
          id={hintId}
          className={cn("text-sm", hintIsError ? "font-semibold text-danger" : "text-fg-muted")}
          role={hintIsError ? "alert" : undefined}
        >
          {hint}
        </p>
      ) : null}
      {error && !hintIsError ? (
        <p id={errorId} role="alert" className="text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-md border border-danger-muted bg-danger-subtle px-4 py-3 text-sm text-danger-fg">
      {message}
    </div>
  );
}

export function FormSuccess({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div role="status" className="rounded-md border border-secondary-muted bg-secondary-subtle px-4 py-3 text-sm text-heading">
      {message}
    </div>
  );
}
